import React, {ReactElement} from 'react';
import {Helmet} from 'react-helmet';
import {useStaticQuery, graphql} from 'gatsby';
import {useIntl} from 'react-intl';

import {useLocale} from '../RootProvider/LocaleContext';
import getHrefLanguageFromLocale from '../localeUtils/getHrefLanguageFromLocale';

interface Props {
  description?: string;
  meta?: {
    name: string;
    content: string;
  }[];
  title?: string;
  link?: {
    rel: string;
    href: string;
  }[];
  scripts?: {
    type: string;
    innerHTML: string;
  }[];
}

function SEO({description = '', meta = [], title, link = [], scripts}: Props): ReactElement {
  const {formatMessage} = useIntl();
  const {site} = useStaticQuery(
    graphql`
      query SEO {
        site {
          siteMetadata {
            title
            description
            author
          }
        }
      }
    `
  );

  const metaDescription = description || site.siteMetadata.description;

  const lang = getHrefLanguageFromLocale(useLocale());

  return (
    <Helmet
      defaultTitle={formatMessage({id: 'seo.defaultMetaTitle'})}
      htmlAttributes={{
        lang,
      }}
      title={title}
      titleTemplate={`%s | ${site.siteMetadata.title}`}
      meta={[
        {
          name: 'description',
          content: metaDescription,
        },
        {
          property: 'og:title',
          content: title,
        },
        {
          property: 'og:description',
          content: metaDescription,
        },
        {
          property: 'og:type',
          content: 'website',
        },
        {
          name: 'twitter:card',
          content: 'summary',
        },
        {
          name: 'twitter:creator',
          content: site.siteMetadata.author,
        },
        {
          name: 'twitter:title',
          content: title,
        },
        {
          name: 'twitter:description',
          content: metaDescription,
        },
      ].concat(meta)}
      link={link}
      script={scripts}
    />
  );
}

export default SEO;
